import PropTypes from 'prop-types';
import STEP2_COPY from './step2/copy';
import { CarteCAE, CarteMicro, CarteEI, CarteSASU } from './step2/cards';
import { DetailChargesCAE, DetailAutresCharges } from './step2/details';

const STATUTS_MAP = {
  micro: CarteMicro,
  ei: CarteEI,
  sasu: CarteSASU,
};

function Step2Resultats({
  data,
  resultatCAE,
  resultatMicro,
  resultatEI,
  resultatSASU,
  statutComparaison,
  onChangeStatut,
  onNext,
  onPrevious,
  onPrint,
}) {
  const statutActif = statutComparaison || STEP2_COPY.statuses[0].id;
  const CarteComparaison = STATUTS_MAP[statutActif];
  const resultatComparaison = statutActif === 'micro' ? resultatMicro : statutActif === 'ei' ? resultatEI : resultatSASU;
  const carteProps = {
    resultat: resultatComparaison,
    metier: data.metier,
    isTTC: data.isTTC,
    caTTC: data.caTTC,
    caHT: data.caHT,
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="bg-white rounded-xl shadow-card p-6 md:p-8">
        <div className="mb-6">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {STEP2_COPY.header.title}
          </h2>
          <p className="text-gray-600">{STEP2_COPY.header.description}</p>
        </div>

        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <span className="text-amber-500 text-lg leading-none mt-0.5">ℹ️</span>
          <p className="text-sm text-amber-800">{STEP2_COPY.header.disclaimer}</p>
        </div>

        <div className="mb-6 bg-gray-50 rounded-lg p-4">
          <p className="text-sm font-medium text-gray-700 mb-3">{STEP2_COPY.selector.label}</p>
          <div className="flex flex-wrap gap-3">
            {STEP2_COPY.statuses.map((statut) => (
              <label
                key={statut.id}
                className={[
                  'flex items-center gap-2 cursor-pointer px-4 py-2 rounded-lg border-2 transition-colors',
                  statutActif === statut.id
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-primary-300',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="statut-comparaison"
                  value={statut.id}
                  checked={statutActif === statut.id}
                  onChange={() => onChangeStatut(statut.id)}
                  className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                />
                <span className="text-xl">{statut.icon}</span>
                <span className="font-medium">{statut.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <CarteCAE resultat={resultatCAE} metier={data.metier} caHT={data.caHT} isTTC={data.isTTC} caTTC={data.caTTC} />
          {CarteComparaison && <CarteComparaison {...carteProps} />}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <DetailChargesCAE resultat={resultatCAE} />
          <DetailAutresCharges
            resultat={resultatComparaison}
            metier={data.metier}
            statut={statutActif}
            charges={data.chargesProfessionnellesHorsCAE}
          />
        </div>

        <div className="flex flex-col sm:flex-row justify-between gap-4 pt-6 border-t border-gray-200">
          <button
            onClick={onPrevious}
            className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>{STEP2_COPY.navigation.previous}</span>
          </button>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onPrint}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 border border-gray-300"
            >
              {STEP2_COPY.navigation.print}
            </button>

            <button
              onClick={onNext}
              className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white font-bold text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-3"
            >
              <span>{STEP2_COPY.navigation.next}</span>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

Step2Resultats.propTypes = {
  data: PropTypes.shape({
    metier: PropTypes.string.isRequired,
    caHT: PropTypes.number.isRequired,
    caTTC: PropTypes.number.isRequired,
    chargesProfessionnellesHorsCAE: PropTypes.object,
  }).isRequired,
  resultatCAE: PropTypes.object.isRequired,
  // Seul le statut de comparaison affiché est calculé en étape 2 ; les autres peuvent être absents.
  resultatMicro: PropTypes.object,
  resultatEI: PropTypes.object,
  resultatSASU: PropTypes.object,
  statutComparaison: PropTypes.oneOf(STEP2_COPY.statuses.map((s) => s.id)).isRequired,
  onChangeStatut: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onPrint: PropTypes.func.isRequired,
};

export default Step2Resultats;
